<script>
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import flatPickr from 'vue-flatpickr-component';
import MaskedInput from 'vue-text-mask';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Layout from '@layouts/main';
import BlockingContainer from '@components/BlockingContainer';
import FileWrapper from '@components/FileWrapper';
import SingleImageCrop from '@components/SingleImageCrop';
import datePickerUtility from '@mixins/datePickerUtility';

export default {
  page() {
    return {
      title: this.$t('titles.register'),
    };
  },
  components: {
    Layout,
    BlockingContainer,
    FontAwesomeIcon,
    flatPickr,
    MaskedInput,
    FileWrapper,
    SingleImageCrop,
  },
  mixins: [datePickerUtility],
  data() {
    return {
      user: {
        first_name: null,
        last_name: null,
        email: null,
        password: null,
        password_confirmation: null,
        sex: 1,
        dob: null,
        mobile_number: null,
        street: null,
        postal_code: null,
        town: null,
        country: null,
        nationality: null,
        id_card_number: null,
        driving_license_number: null,
        transport_license_date: null,
        bank_name: null,
        iban: null,
        bic: null,
        id_card: null,
        driving_license: null,
        transport_license: null,
        avatar: {
          id: Math.random(),
          original: null,
          meta_data: {
            height: 0,
            scaleX: 1,
            scaleY: 1,
            width: 0,
            x: 0,
            y: 0,
          },
        },
      },
      avatarUploadShow: false,
      status: 0,
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
      dOBDateConfig: (state) =>
              Object.assign({}, state.common.dateConfig, {
                maxDate: DateTime.local()
                        .minus({ years: 18 })
                        .toFormat('yyyy-LL-dd'),
              }),
    }),
  },
  methods: {
    validate() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('register');
          this.register();
        }
      });
    },
    register() {
      this.$store.dispatch('auth/register', {
        router: this.$router,
        redirect: 'home.index',
        user: Object.assign({}, this.user, {
          id_card: this.user.id_card ? this.user.id_card[0] : null,
          driving_license: this.user.driving_license ? this.user.driving_license[0] : null,
          transport_license: this.user.transport_license ? this.user.transport_license[0] : null,
        }),
        successCallback: () => this.$wait.end('register'),
        callback: () => this.$wait.end('register'),
      });
    },
    metaUpdateImage(entity, meta) {
      this.$set(this.user[entity], 'meta_data', meta);
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="justify-content-center">
      <b-col md="6">
        <b-card
          :border-variant="!errors.items.length ? 'dark' : 'danger'"
          :header="$t('titles.register')"
          header-tag="header"
          :header-bg-variant="!errors.items.length ? 'dark' : 'danger'"
          header-text-variant="white"
        >
          <BlockingContainer v-if="$wait.is('register')"></BlockingContainer>
          <b-form @submit.prevent="validate">
            <b-form-row>
              <b-col sm="6">
                <b-form-group>
                  <template v-slot:label class="col-form-legend col-12">
                    {{ $t('fields.first_name') }} <span class="status-star text-danger">*</span>
                  </template>
                  <b-form-input
                    id="first_name"
                    v-model="user.first_name"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="first_name"
                    :data-vv-as="$t('fields.first_name')"
                    type="text"
                    :state="errors.has('first_name') ? false : null"
                    aria-describedby="input-feedback-first_name"
                    :placeholder="$t('placeholders.first_name')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-first_name">
                    <template v-show="errors.has('first_name')">{{ errors.first('first_name') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group>
                   <template v-slot:label class="col-form-legend col-12">
                    {{ $t('fields.last_name') }} <span class="status-star text-danger">*</span>
                  </template>
                  <b-form-input
                    id="last_name"
                    v-model="user.last_name"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="last_name"
                    :data-vv-as="$t('fields.last_name')"
                    type="text"
                    :state="errors.has('last_name') ? false : null"
                    aria-describedby="input-feedback-last_name"
                    :placeholder="$t('placeholders.last_name')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-last_name">
                    <template v-show="errors.has('last_name')">{{ errors.first('last_name') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col sm="6">
                <SingleImageCrop
                  v-if="avatarUploadShow"
                  v-model="user.avatar.original"
                  :meta-data="user.avatar.meta_data"
                  :init="true"
                  :clear-button-container="$style.clearButtonContainer"
                  dropzone-class="fileUploadZoneSmall"
                  image-resize-height="155px"
                  clear-button="btn-sm"
                  @meta-update="metaUpdateImage('avatar', ...arguments)"
                ></SingleImageCrop>
                <template v-else>
                  <img
                    v-if="!user.avatar.original"
                    class="img-thumbnail mx-auto d-block rounded-circle"
                    :class="$style.avatarImg"
                    src="@assets/images/none.png"
                    alt="Avatar"
                    @click="avatarUploadShow = true"
                  />
                  <img
                    v-else
                    :key="user.avatar.large"
                    class="img-thumbnail mx-auto d-block rounded-circle"
                    :class="$style.avatarImg"
                    :src="user.avatar.large"
                    alt="Avatar"
                    @click="avatarUploadShow = true"
                  />
                </template>
              </b-col>
            </b-form-row>

            <b-form-group>
              <b-form-radio-group
                v-model="user.sex"
                v-validate="'required'"
                data-vv-value-path="localValue"
                data-vv-name="sex"
                :data-vv-as="$t('fields.gender')"
                aria-describedby="input-feedback-sex"
              >
                <b-form-radio :value="1">{{ $t('fields.sex.1') }}</b-form-radio>
                <b-form-radio :value="2">{{ $t('fields.sex.2') }}</b-form-radio>
                <b-form-radio :value="3">{{ $t('fields.sex.3') }}</b-form-radio>
              </b-form-radio-group>
              <b-form-invalid-feedback id="input-feedback-sex">
                <template v-show="errors.has('sex')">{{ errors.first('sex') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.email') }} <span class="status-star text-danger">*</span>
              </template>
              <b-form-input
                id="email"
                v-model="user.email"
                v-validate="'required|email|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="email"
                :data-vv-as="$t('fields.email')"
                type="text"
                :state="errors.has('email') ? false : null"
                aria-describedby="input-feedback-email"
                :placeholder="$t('placeholders.email')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-email">
                <template v-show="errors.has('email')">{{ errors.first('email') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.password') }} <span class="status-star text-danger">*</span>
              </template>
              <input
                ref="password"
                v-model="user.password"
                v-validate="'required|min:8|max:255|verify_password'"
                name="password"
                class="form-control"
                data-vv-name="password"
                :data-vv-as="$t('fields.password')"
                type="password"
                :class="errors.has('password') ? 'is-invalid' : null"
                aria-describedby="input-feedback-password"
                :placeholder="$t('placeholders.password')"
              />

              <b-form-invalid-feedback id="input-feedback-password">
                <template v-show="errors.has('password')">{{ errors.first('password') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.repeat_password') }} <span class="status-star text-danger">*</span>
              </template>
              <input
                v-model="user.password_confirmation"
                v-validate="'required|confirmed:password'"
                :data-vv-as="$t('fields.password_confirmation')"
                type="password"
                name="password_confirmation"
                class="form-control"
                :class="errors.has('password_confirmation') ? 'is-invalid' : null"
                aria-describedby="input-feedback-password_confirmation"
                :placeholder="$t('placeholders.password_confirmation')"
              />

              <b-form-invalid-feedback id="input-feedback-password_confirmation">
                <template v-show="errors.has('password_confirmation')">{{ errors.first('password_confirmation') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.dob')}:`">
              <flat-pickr
                v-model="user.dob"
                v-validate="'required'"
                data-vv-name="dob"
                :data-vv-as="$t('fields.dob')"
                aria-describedby="input-feedback-dob"
                :config="dOBDateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('dob') }"
                :placeholder="$t('placeholders.date')"
                @on-close="closeCalendar('user.dob', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-dob">
                <template v-show="errors.has('dob')"
                  >{{ errors.first('dob') }}
                </template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.mobile_number') }} <span class="status-star text-danger">*</span>
              </template>
              <b-form-input
                v-model="user.mobile_number"
                v-validate="'required|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="mobile_number"
                :data-vv-as="$t('fields.mobile_number')"
                type="text"
                :state="errors.has('mobile_number') ? false : null"
                aria-describedby="input-feedback-mobile_number"
                :placeholder="$t('placeholders.mobile_number')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-mobile_number">
                <template v-show="errors.has('mobile_number')">{{ errors.first('mobile_number') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-row>
              <b-col>
                <b-form-group :label="`${$t('fields.street')}:`">
                  <b-form-input
                    id="street"
                    v-model="user.street"
                    v-validate="'required|max:65000'"
                    data-vv-value-path="localValue"
                    data-vv-name="street"
                    :data-vv-as="$t('fields.street')"
                    type="text"
                    :state="errors.has('street') ? false : null"
                    aria-describedby="input-feedback-street"
                    :placeholder="$t('placeholders.street')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-street">
                    <template v-show="errors.has('street')">{{ errors.first('street') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="`${$t('fields.postal_code')}:`">
                  <b-form-input
                    id="postal_code"
                    v-model="user.postal_code"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="postal_code"
                    :data-vv-as="$t('fields.postal_code')"
                    type="text"
                    :state="errors.has('postal_code') ? false : null"
                    aria-describedby="input-feedback-postal_code"
                    :placeholder="$t('placeholders.postal_code')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-postal_code">
                    <template v-show="errors.has('postal_code')">{{ errors.first('postal_code') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col>
                <b-form-group :label="`${$t('fields.town')}:`">
                  <b-form-input
                    id="town"
                    v-model="user.town"
                    v-validate="'required|max:65000'"
                    data-vv-value-path="localValue"
                    data-vv-name="town"
                    :data-vv-as="$t('fields.town')"
                    type="text"
                    :state="errors.has('town') ? false : null"
                    aria-describedby="input-feedback-town"
                    :placeholder="$t('placeholders.town')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-town">
                    <template v-show="errors.has('town')">{{ errors.first('town') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="`${$t('fields.country')}:`">
                  <b-form-input
                    id="country"
                    v-model="user.country"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="country"
                    :data-vv-as="$t('fields.country')"
                    type="text"
                    :state="errors.has('country') ? false : null"
                    aria-describedby="input-feedback-country"
                    :placeholder="$t('placeholders.country')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-country">
                    <template v-show="errors.has('country')">{{ errors.first('country') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-group :label="`${$t('fields.nationality')}:`">
              <b-form-input
                id="nationality"
                v-model="user.nationality"
                v-validate="'required|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="nationality"
                :data-vv-as="$t('fields.nationality')"
                type="text"
                :state="errors.has('nationality') ? false : null"
                aria-describedby="input-feedback-nationality"
                :placeholder="$t('placeholders.nationality')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-nationality">
                <template v-show="errors.has('nationality')">{{ errors.first('nationality') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.id_card_number') }}:
                <span class="near-label-info text-secondary" @click.stop>
                  <FontAwesomeIcon id="idCardNumberPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                  <b-popover target="idCardNumberPopover" triggers="focus" placement="rightbottom">
                    {{ $t('infos.id_card_number') }}
                  </b-popover>
                </span>
              </template>
              <b-form-input
                v-model="user.id_card_number"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="id_card_number"
                :data-vv-as="$t('fields.id_card_number')"
                type="text"
                :state="errors.has('id_card_number') ? false : null"
                aria-describedby="input-feedback-id_card_number"
                :placeholder="$t('placeholders.id_card_number')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-id_card_number">
                <template v-show="errors.has('id_card_number')">{{ errors.first('id_card_number') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.id_card')}:`">
              <FileWrapper
                v-model="user.id_card"
                v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
                :data-vv-as="$t('fields.id_card')"
                :state="errors.has('id_card') ? false : null"
                :placeholder="$t('placeholders.id_card')"
                accept="image/*,application/pdf"
                aria-describedby="input-feedback-id_card"
                name="id_card"
              >
              </FileWrapper>

              <div v-show="errors.has('id_card')" class="invalid-feedback d-block mt-1">
                {{ errors.first('id_card') }}
              </div>
            </b-form-group>

            <b-form-group :label="`${$t('fields.driving_license_number')}:`">
              <b-form-input
                v-model="user.driving_license_number"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="driving_license_number"
                :data-vv-as="$t('fields.driving_license_number')"
                type="text"
                :state="errors.has('driving_license_number') ? false : null"
                aria-describedby="input-feedback-driving_license_number"
                :placeholder="$t('placeholders.driving_license_number')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-driving_license_number">
                <template v-show="errors.has('driving_license_number')">{{ errors.first('driving_license_number') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.driving_license')}:`">
              <FileWrapper
                v-model="user.driving_license"
                v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
                :data-vv-as="$t('fields.driving_license')"
                :state="errors.has('driving_license') ? false : null"
                :placeholder="$t('placeholders.driving_license')"
                accept="image/*,application/pdf"
                aria-describedby="input-feedback-driving_license"
                name="driving_license"
              >
              </FileWrapper>

              <div v-show="errors.has('driving_license')" class="invalid-feedback d-block mt-1">
                {{ errors.first('driving_license') }}
              </div>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.transport_license_date') }}:
                <span class="near-label-info text-secondary" @click.stop>
                  <FontAwesomeIcon id="transportLicenseDatePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                  <b-popover target="transportLicenseDatePopover" triggers="focus" placement="rightbottom">
                    {{ $t('infos.transport_license_date') }}
                  </b-popover>
                </span>
              </template>
              <flat-pickr
                      v-model="user.transport_license_date"
                      v-validate="''"
                      data-vv-value-path="localValue"
                      data-vv-name="transport_license_date"
                      :data-vv-as="$t('fields.transport_license_date')"
                      aria-describedby="input-feedback-transport_license_date"
                      :config="dateConfig"
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('transport_license_date') }"
                      :placeholder="$t('placeholders.transport_license_date')"
                      @on-close="closeCalendar('user.transport_license_date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)">
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-transport_license_date">
                <template v-show="errors.has('transport_license_date')">{{ errors.first('transport_license_date') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.transport_license')}:`">
              <FileWrapper
                v-model="user.transport_license"
                v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
                :data-vv-as="$t('fields.transport_license')"
                :state="errors.has('transport_license') ? false : null"
                :placeholder="$t('placeholders.transport_license')"
                accept="image/*,application/pdf"
                aria-describedby="input-feedback-transport_license"
                name="transport_license"
              >
              </FileWrapper>

              <div v-show="errors.has('transport_license')" class="invalid-feedback d-block mt-1">
                {{ errors.first('transport_license') }}
              </div>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.bank_name') }} <span class="status-star text-danger">*</span>
              </template>
              <b-form-input
                id="bank_name"
                v-model="user.bank_name"
                v-validate="'required|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="bank_name"
                :data-vv-as="$t('fields.bank_name')"
                type="text"
                :state="errors.has('bank_name') ? false : null"
                aria-describedby="input-feedback-bank_name"
                :placeholder="$t('placeholders.bank_name')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-bank_name">
                <template v-show="errors.has('bank_name')">{{ errors.first('bank_name') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-row>
              <b-col sm="7">
                <b-form-group>
                  <template v-slot:label class="col-form-legend col-12">
                    {{ $t('fields.iban') }} <span class="status-star text-danger">*</span>
                  </template>
                  <MaskedInput
                    v-model="user.iban"
                    v-validate="'required|max:27'"
                    data-vv-value-path="localValue"
                    data-vv-name="iban"
                    :data-vv-as="$t('fields.iban')"
                    type="text"
                    name="phone"
                    class="form-control"
                    :mask="[
                      /[A-Z]/i,
                      /[A-Z]/i,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                    ]"
                    :guide="true"
                    :pipe="(conformedValue) => conformedValue.toUpperCase()"
                    placeholder-char="_"
                    aria-describedby="input-feedback-iban"
                    :class="errors.has('iban', 'profile') ? 'is-invalid' : null"
                    placeholder="DE12 3456 7890 1234 5678 91"
                  >
                  </MaskedInput>

                  <b-form-invalid-feedback id="input-feedback-iban">
                    <template v-show="errors.has('iban')">{{ errors.first('iban') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group>
                  <template v-slot:label class="col-form-legend col-12">
                    {{ $t('fields.bic') }}:
                    <span class="near-label-info text-secondary" @click.stop>
                      <FontAwesomeIcon id="bicPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                      <b-popover target="bicPopover" triggers="focus" placement="rightbottom">
                        {{ $t('infos.bic') }}
                      </b-popover>
                    </span>
                  </template>
                  <b-form-input
                    id="bic"
                    v-model="user.bic"
                    v-validate="'max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="bic"
                    :data-vv-as="$t('fields.bic')"
                    type="text"
                    :state="errors.has('bic') ? false : null"
                    aria-describedby="input-feedback-bic"
                    :placeholder="$t('placeholders.bic')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-bic">
                    <template v-show="errors.has('bic')">{{ errors.first('bic') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-group>
              <b-form-checkbox
                v-model="status"
                v-validate="'required|is:1'"
                data-vv-value-path="localValue"
                data-vv-name="terms"
                name="terms"
                value="1"
                unchecked-value="0"
                :state="errors.has('terms') ? false : null"
                aria-describedby="input-feedback-terms"
              >
                <i18n path="term" tag="span" for="tos">
                  <a href="http://www.kks-hahn.de/datenschutz" target="_blank">{{ $t('tos') }}</a>
                </i18n>
              </b-form-checkbox>
              <div>
                <small v-show="errors.has('terms')" class="text-danger">{{ $t('errors.terms') }}</small>
              </div>
            </b-form-group>

            <b-button :disabled="!!errors.items.length" type="submit" :variant="'outline-' + (!errors.items.length ? 'dark' : 'danger')"
              >{{ $t('actions.submit') }}
            </b-button>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.avatarImg {
  height: 160px;
  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
